export function inView(target) {

  const thresholdDefault = 0.5;
  const activeClassDefault = 'is-in-view';
  const inactiveClassDefault = 'is-out-of-view';
  const activeStickyClass = 'in-view-is-sticky';
  const initViewClassDefault = 'in-view-init'; // applied the first time comes into viewport
  const disableClass = 'in-view-disable';
  const delayInitDefault = 0; // delay adding init class after in viewport
  const delayActivateDefault = 0; // delay adding active class after in viewport

  let observer;

  const options = (threshold = thresholdDefault) => ({
    root: null,
    rootMargin: '0px',
    threshold: [0, 0.025, 0.05, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1]
  });

  function makeCallback(
    threshold = thresholdDefault, 
    activeClass = activeClassDefault, 
    inactiveClass = inactiveClassDefault,
    initViewClass = initViewClassDefault,
    delayActivate = delayActivateDefault,
    delayOnload = 0, // delay adding init class to element that is in viewport on page load only
  ) {
    
    return entries => {

      entries.forEach((entry) => {

        const targetEl = entry.target;
        const targetElClassList = targetEl.classList;
        const targetElDataset = entry.target.dataset;
        const thresholdCheck = parseFloat(targetElDataset.inviewThreshold || threshold);
        const delayActive = targetElDataset.inviewDelayActive || delayActivate;
        const delayInit = targetElDataset.inviewDelayInit || delayInitDefault;
        const delayOnload = targetElDataset.inviewDelayOnload;
        const propagateTargetEl = targetEl.querySelectorAll(targetElDataset.inviewPropagateTarget);

        function applyClasses(classList, targetEl) {
          // Add init view class only first time element comes into view
          if (!classList.contains(activeClass) && !classList.contains(inactiveClass)) {

            if (delayOnload) {
              window.setTimeout(() => {
                window.dispatchEvent(new CustomEvent('inView', { detail: { statusDescription: 'in-view-init', status: true, element: targetEl }}));
                classList.add(initViewClass);
                delete targetEl.dataset['inviewDelayOnload'];
              }, delayOnload * 1000)
            } 
            else {
              window.setTimeout(() => {
                window.dispatchEvent(new CustomEvent('inView', { detail: { statusDescription: 'in-view-init', status: true, element: targetEl }}));
                classList.add(initViewClass);
              }, delayInit * 1000)
            }
          }

          window.setTimeout(() => {
            window.dispatchEvent(new CustomEvent('inView', { detail: { statusDescription: 'in-view', status: true, element: targetEl }}));
            classList.add(activeClass);
          }, delayActive * 1000)

          if (inactiveClass) {
            classList.remove(inactiveClass);
          }
        }
        
        // If element has css rule - position: sticky - add class to know when it is in sticky mode
        // Add attribute in html - data-inview-is-sticky="true"
        if (targetElDataset.inviewIsSticky) {
          if (entry.intersectionRatio >= 1) {
            targetElClassList.add(activeStickyClass);
          } else {
            targetElClassList.remove(activeStickyClass);
          }
        }

        // Control opacity of element using threshold value when entering and leaving viewport 
        // Add attribute in html - data-inview-control-opacity="true"
        if (targetElDataset.inviewControlOpacity) {
          // If is part of a sticky stack and the data-inview-is-sticky="true" attr is being used
          // the previous 'stuck' element opacity will be updated and not the current scrolling element
          if (targetElDataset.inviewIsSticky) {
            const previousEl = targetEl.previousElementSibling;
            if (previousEl && previousEl.firstElementChild) {
              previousEl.firstElementChild.style.opacity = 1 - entry.intersectionRatio;
            } 
          } else {
            targetEl.style.opacity = entry.intersectionRatio;
          }
        }

        // In view
        if (entry.intersectionRatio >= thresholdCheck) {
          if (propagateTargetEl && propagateTargetEl.length) {
            for (let i = 0; i < propagateTargetEl.length; i++) {
              if (!propagateTargetEl[i].classList.contains(disableClass)) {
                applyClasses(propagateTargetEl[i].classList, propagateTargetEl[i]);
              }
            }
          }
          if (!targetElClassList.contains(disableClass)) {
            applyClasses(targetElClassList, targetEl);
          }
        } 
        // Out of view
        else if (targetElClassList.contains(activeClass)) {
          window.dispatchEvent(new CustomEvent('inView', { detail: { statusDescription: 'out-of-view', status: false, element: targetEl }}));
          targetElClassList.add(inactiveClass);
          targetElClassList.remove(activeClass);
          
        } else {
          delete targetEl.dataset['inviewDelayOnload'];
        }
      });
    };
  }

  function init() {

    observer = new IntersectionObserver(
      makeCallback(
        target.threshold, 
        target.activeClass, 
        target.inactiveClass, 
        target.initViewClass,
        target.delayActivate,
        target.delayInit  
      ), 
      options()
    );

    const targetEl = target.selector ? (target.root || document).querySelectorAll(target.selector) : target.root;

    if (targetEl) {
      if (targetEl instanceof NodeList) {
        targetEl.forEach(el => {
          observer.observe(el);
        })  
      } else {
        observer.observe(targetEl);
      }
    } 
  };

  function destroy() {
    observer.disconnect();
  };

  return { 
    init,
    destroy
  };
};

